import React from 'react'
import { compose, lifecycle, withProps } from 'recompose'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import { Column, Row } from '../grid'
import Headline from '../typography/headline'
import Preamble from '../typography/preamble'
import BookingSummary from '../../components/booking-summary'
import { findServiceData } from '../../containers/bada/content'
import { mobileCheck, toSimpleService } from '../../containers/bada/utils'
import Form from './Form'
import { useCms } from '../cms/CmsContext'
import { useHookAsHoc } from '../../hooks/useHookAsHoc'
import { useGetStaffMembers } from '../../../utils/badaAppointmentApi'

const StyledRow = styled(Row)`
  margin-bottom: 0;
  ${(p) => p.theme.media.min.sm`
    margin-bottom: 100px;
  `}
`
const StyledHeadline = styled(Headline)`
  text-transform: inherit;
  text-transform: ${theme('forms.customerForm.headings.textTransform')};
  line-height: ${theme('forms.customerForm.headings.lineHeight')};
  margin-bottom: 0;
  margin-top: ${theme('forms.customerForm.headings.marginTop')};
  font-size: ${theme('forms.customerForm.headings.fontSize')};
  color: ${theme('forms.customerForm.headings.color')};
`

const StyledPreamble = styled(Preamble)`
  margin-top: 10px;
  margin-bottom: 0px;
  font-family: ${theme('forms.customerForm.fontFamily')};
  font-size: ${theme('forms.customerForm.fontSize')};
  font-weight: ${theme('forms.customerForm.fontWeight')};
  color: ${theme('forms.customerForm.color')};
`

const HeaderContainer = styled.div`
  margin-bottom: ${theme('forms.customerForm.margin')};
`

const CustomerForm = ({
  selectedSlot,
  selectedService,
  selectedStore,
  overrideMeetingType,
  values,
  previousQuestions,
  updateUiState,
  addHistory,
  staff,
  flow,
  selectedServiceProvider,
}) => {
  const { pageContent, services } = useCms()
  const service =
    (overrideMeetingType && toSimpleService(overrideMeetingType)) ||
    selectedService
  const serviceData = findServiceData(services, service)

  const mutatedValues = {
    ...values,
    usePrepform: serviceData && serviceData.usePrepform,
  }
  const preselectedPhone = mutatedValues.phone
  const preselectedAddress = mutatedValues.address
  const badaBlockData = JSON.parse(
    window.localStorage.getItem('herculesBadaBlock')
  )
  if (badaBlockData && badaBlockData.email) {
    mutatedValues.email = badaBlockData.email
    mutatedValues.newsletterConsent = !!badaBlockData.optin
  }

  if (!serviceData) {
    return null
  }
  return (
    <>
      <StyledRow id="customer-form">
        <Column sm={12}>
          <HeaderContainer>
            <StyledHeadline>
              {serviceData.formHeading || pageContent.formHeading}
            </StyledHeadline>
            {serviceData.formPreamble && (
              <StyledPreamble>{serviceData.formPreamble}</StyledPreamble>
            )}
          </HeaderContainer>
        </Column>
        <Column sm={6}>
          {
            <Form
              selectedStore={selectedStore}
              selectedSlot={selectedSlot}
              selectedService={service}
              staff={staff}
              updateUiState={updateUiState}
              pageContent={pageContent}
              services={services}
              addHistory={addHistory}
              values={mutatedValues}
              previousQuestions={previousQuestions}
              isBooking={!!selectedSlot && !!selectedSlot.from}
              flow={flow}
              selectedServiceProvider={selectedServiceProvider}
              preselectedAddress={preselectedAddress}
              preselectedPhone={preselectedPhone}
            />
          }
        </Column>
        {!mobileCheck() && (
          <Column sm={6} lg={5} lgOffset={1}>
            <BookingSummary
              store={selectedStore}
              selectedSlot={selectedSlot}
              meetingType={service}
              serviceContent={serviceData}
            />
          </Column>
        )}
      </StyledRow>
    </>
  )
}

CustomerForm.propTypes = {
  updateUiState: PropTypes.func.isRequired,
  addHistory: PropTypes.func.isRequired,
  selectedSlot: PropTypes.shape({
    from: PropTypes.string,
    to: PropTypes.string,
  }),
  selectedService: PropTypes.shape({
    id: PropTypes.string.isRequired,
    serviceId: PropTypes.number,
  }),
  selectedStore: PropTypes.shape({
    id: PropTypes.string,
    simplyBookMeId: PropTypes.number,
  }),
  staff: PropTypes.arrayOf(PropTypes.shape({})),
  values: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    address: PropTypes.string,
  }).isRequired,
  overrideMeetingType: PropTypes.shape({}),
}

CustomerForm.defaultProps = {
  meetingType: null,
  selectedStore: null,
  selectedService: null,
  selectedSlot: null,
  staff: null,
  overrideMeetingType: null,
}

const enhance = compose(
  useHookAsHoc('listStaffMembersForStoreData', ({ selectedStore }) =>
    useGetStaffMembers(selectedStore?.id)
  ),
  withProps(({ listStaffMembersForStoreData }) => ({
    staff: listStaffMembersForStoreData.data,
  })),
  lifecycle({
    componentDidMount() {
      this.props.onMount()
    },
  })
)

export default enhance(CustomerForm)
